import Box from "@mui/material/Box";
import BusinessIcon from "@mui/icons-material/Business";
import ButtonBase from "@mui/material/ButtonBase";
import Link from "next/link";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Typography from "@mui/material/Typography";
import WorkIcon from "@mui/icons-material/Work";
import { Wrapper } from "./Wrapper";
import { useTranslation } from "next-i18next";

/**
 * Turnstile.
 *
 * Helps users route to their final destination relative to requesting access to
 * the portals.
 *
 * Please review the original implementation in `<SelectAccess />`.
 * @see https://git.cglcloud.com/CustomerExperience/cgl-dxo-fe-mycargill-platform/blob/development/src/apps/requestaccess/presentation/SelectAccess/index.js
 *
 * Please see, _what is a turnstile?_
 * @see https://en.wikipedia.org/wiki/Turnstile
 *
 * @returns ui for selecting a portal
 */
export function Turnstile() {
  const { t } = useTranslation();
  return (
    <>
      <Wrapper>
        <Box
          sx={{
            marginBottom: "20px",
            marginTop: "48px",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: "1.75em",
              fontWeight: "bold",
            }}
          >
            {t("request_access:select_access_type.title")}
          </Typography>
        </Box>
        <Box sx={{ marginBottom: "48px", fontSize: "1em" }}>
          {t("request_access:select_access_type.summary")}
        </Box>

        <Box
          sx={{
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 6,
            justifyContent: "space-between",
            alignItems: "stretch",
            margin: 0,
            padding: 0,
            px: 8,
            a: {
              width: "100%",
              display: "block",
              textDecoration: "none",
              color: "#000",
              transition: "0.3s ease-in-out",
              "&:hover": {
                boxShadow:
                  "0 6px 9px rgb(0 0 0 / 14%), 0 2px 18px rgb(0 0 0 / 10%), 0 4px 6px rgb(0 0 0 / 10%)",
                background: "#f4f4f4",
              },
            },
          }}
          component="ul"
        >
          <li>
            <Link href="/request-access/customer">
              <TurnstileButton
                icon={<BusinessIcon fontSize="large" />}
                title={t(
                  "request_access:select_access_type.current_customer_title"
                )}
                summary={t(
                  "request_access:select_access_type.current_customer_summary"
                )}
              />
            </Link>
          </li>
          <li>
            <Link href="/request-access/employee">
              <TurnstileButton
                icon={<WorkIcon fontSize="large" />}
                title={t("request_access:select_access_type.employee_title")}
                summary={t(
                  "request_access:select_access_type.employee_summary"
                )}
              />
            </Link>
          </li>
          <li>
          <Link href="/request-access/new-customer">
              <TurnstileButton
                icon={<PersonAddIcon fontSize="large" />}
                title={t("request_access:select_access_type.new_customer_title")}
                summary={t(
                  "request_access:select_access_type.new_customer_summary"
                )}
              />
            </Link>
          </li>
        </Box>
      </Wrapper>
    </>
  );
}

interface TurnstileButtonProps {
  icon: React.ReactNode;
  title: React.ReactNode;
  summary: React.ReactNode;
}

function TurnstileButton(props: TurnstileButtonProps) {
  const { icon, title, summary } = props;
  return (
    <ButtonBase
      component="div"
      sx={{
        border: "1px solid #ddd",
        padding: "1rem 2rem 1rem 1rem",
        textAlign: "left",
        width: "100%",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Box>{icon}</Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box sx={{ fontSize: "1.25rem", fontWeight: "bold" }}>{title}</Box>
          <Box sx={{ fontSize: ".9rem" }}>{summary}</Box>
        </Box>
      </Box>
    </ButtonBase>
  );
}
